import React from 'react';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { StreamCard } from 'flows/core/components/cards/common/stream-card';
import { CardData, CardDataWithHeroExtras } from 'flows/core/types/cards';
import { getIconComponent } from 'utils/get-icon';
import { Badges } from 'flows/core/utils/card-makers/utils';
import { useFeature } from '@growthbook/growthbook-react';
import { TVIPromoCard } from 'flows/core/components/cards/tvi-promo-card';
import { FYMPromoCard } from 'flows/core/components/cards/fym-promo-card';
import { SYIPromoCard } from 'flows/core/components/cards/syi-promo-card';
import { UTILSPromoCard } from 'flows/core/components/cards/utils-promo-card';
import { ImagePromoCard } from 'flows/core/components/cards/image-promo-card';
import { ReceiptCheckBackLaterCard } from 'flows/core/components/cards/receipt-card';
import { TviReceiptCard } from 'flows/core/components/cards/receipt-card/TviReceiptCard';

const iconTestId = 'essentials-card-icon';

const EssentialsInitiateCard: React.FC<
  Extract<CardData, { type: 'INITIATE' }>
> = ({ data, identifier }) => {
  const { on: requiredBadgeEnabled } = useFeature('essentials_required_badge');
  const { icon, title, badge: cardBadge, action, description, required } = data;

  return (
    <StreamCard
      icon={getIconComponent(icon, iconTestId) ?? <></>}
      heading={title}
      description={description}
      badge={required && requiredBadgeEnabled ? Badges.REQUIRED : undefined}
      badge2={cardBadge}
      identifier={identifier}
      cardType="INITIATE"
      action={action}
      paddingSize={{ mobile: 's', desktop: 'm' }}
    />
  );
};

const EssentialsReceiptCard: React.FC<
  Extract<CardData, { type: 'RECEIPT' }>
> = ({ data, identifier, type }) => {
  if (data.subtype !== 'BASIC') return null;
  const { heading, badge, action, description } = data;

  return (
    <StreamCard
      identifier={identifier}
      cardType={type}
      heading={heading ?? ''}
      description={description}
      badge={badge}
      action={action}
      paddingSize={{ mobile: 's', desktop: 'm' }}
    />
  );
};

const EssentialsCardMapper: React.FC<{
  card: CardData | CardDataWithHeroExtras;
}> = ({ card }) => {
  switch (card.type) {
    case 'PROMOTED':
      switch (card.data.subtype) {
        case 'TVI':
          return <TVIPromoCard {...card} />;
        case 'FYM':
          return <FYMPromoCard {...card} />;
        case 'SYI':
          return <SYIPromoCard {...card} />;
        case 'UTIL':
          return <UTILSPromoCard {...card} />;
        case 'IMAGE':
          return <ImagePromoCard {...card} />;
        default:
          return null;
      }
    case 'INITIATE':
      return <EssentialsInitiateCard {...card} />;
    case 'RECEIPT':
      switch (card.data.subtype) {
        case 'BASIC':
          return <EssentialsReceiptCard {...card} />;
        case 'CHECKBACKLATER':
          return <ReceiptCheckBackLaterCard {...card} />;
        case 'TVI':
          return <TviReceiptCard {...card} />;
        default:
          return null;
      }
    default:
      return null;
  }
};

export const EssentialsCard: React.FC<{
  card: CardData | CardDataWithHeroExtras;
  position?: number | string;
}> = ({ card, position }) => {
  const { Track } = useTracking<TrackEventInput<unknown>>({
    object: 'card',
    details: { card, position },
  });
  return (
    <Track>
      <EssentialsCardMapper card={card} />
    </Track>
  );
};
